import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { Button, CheckMarkIcon, CloseIcon } from '@blade/ui-kit'
import { UserShort } from '@root/entities/user-short'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { useTranslation } from '@root/shared/localization'
import {
  ChevronLeftIcon,
  Clock3Icon,
  CheckCircleThinIcon,
  mediaMaxMobile,
} from '@root/shared/ui-kit'

import { supportDialogScreenAtom } from '../atoms'

import type { SupportTicket } from '../model/support-ticket'
import type { ClassNameProps } from '@root/shared/lib/react'

type SupportDialogHeadingProps = {
  onClose?: () => void
  ticket: SupportTicket
} & ClassNameProps

export const SupportDialogHeadingAdmin: React.FC<SupportDialogHeadingProps> = ({
  className,
  onClose,
  ticket,
}) => {
  const { t } = useTranslation()
  const [, setScreen] = useAtom(supportDialogScreenAtom)
  const [status] = useAtom(ticket.status)
  const toggleStatus = useAction(ticket.toggleStatus)
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)

  const handleBack = () => setScreen({ screen: 'tickets' })
  const handleCloseTicket = () => {
    setIsStatusUpdating(true)
    void toggleStatus().finally(() => setIsStatusUpdating(false))
  }

  const TicketIcon = status === 'active' ? OpenTicketIcon : ClosedTicketIcon

  return (
    <Root className={className}>
      <ReturnIcon size={14} onClick={handleBack} />
      <UserAvatarBg>
        <UserShortAvatar
          userShort={ticket.author}
          size={25}
          outlineSpacingWidth={0.5}
          isOnline={ticket.author.isOnline}
        />
      </UserAvatarBg>
      <TitleGroup>
        <Title>{UserShort.someName(ticket.author)}</Title>
        <Subtitle>
          <SubtitleIcon>
            <TicketIcon size={10} />
          </SubtitleIcon>
          <SubtitleText>{ticket.subject}</SubtitleText>
        </Subtitle>
      </TitleGroup>
      <ButtonCloseTicket
        size="h32"
        variant="secondary"
        radius="rounded"
        iconLeft={<CheckMarkIcon size={12} />}
        onClick={handleCloseTicket}
        busy={isStatusUpdating}
      >
        {t(`support.${status === 'active' ? 'close-ticket' : 'reopen-ticket'}`)}
      </ButtonCloseTicket>
      {onClose ? (
        <ButtonClose
          size="s"
          variant="ghost"
          iconRight={<CloseIcon />}
          onClick={onClose}
        />
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr auto auto;
  align-content: center;
  align-items: center;

  padding-inline-start: 14px;

  gap: 6px;

  border-bottom: 1px solid var(--background-3);

  ${mediaMaxMobile(css`
    padding-inline-end: 20px;
  `)}
`

const TitleGroup = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`

const UserAvatarBg = styled.div`
  background: var(--appearance-1);
  padding: 2px;
  border-radius: 50%;
  height: 29px;
`

const Title = styled.div`
  color: var(--text-12);

  font-size: 12px;
  font-style: normal;
  font-weight: 600;

  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Subtitle = styled.div`
  color: var(--text-11);
  font-size: 10px;
  font-style: normal;
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
`

const SubtitleIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
`

const SubtitleText = styled.span`
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ButtonClose = styled(Button)`
  color: var(--text-9);

  &:hover {
    color: var(--text-12);
    background: transparent;
  }
`

const ReturnIcon = styled(ChevronLeftIcon)`
  color: var(--text-10);
  cursor: pointer;
`

const ButtonCloseTicket = styled(Button)`
  font-weight: 500;
  margin-block: 10px;
  margin-inline-end: -8px;
`

const OpenTicketIcon = styled(Clock3Icon)`
  color: var(--amber-9);
`

const ClosedTicketIcon = styled(CheckCircleThinIcon)`
  color: var(--green-9);
`
